<!-- TabDeleteConfirmModal.vue -->
<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <!-- 모달 헤더 -->
      <div class="modal-header">
        <h3 class="modal-title">평가차수 삭제 확인</h3>
      </div>

      <!-- 모달 내용 -->
      <div class="modal-content">
        <div class="warning-messages">
          <p class="warning-text">평가자가 평가를 진행한 이력이 존재합니다.</p>
          <p class="warning-text">삭제 진행시 기존에 진행한 평가를 조회할 수 없습니다.</p>
          <p class="warning-text">평가차수를 삭제하시겠습니까?</p>
        </div>

        <div class="input-section">
          <label class="input-label">삭제 확인</label>
          <input
            ref="confirmInput"
            v-model="confirmText"
            type="text"
            class="confirm-input"
            :placeholder="`삭제 하려는 평가명을 동일하게 입력해 주세요. (${tabLabel})`"
            @keydown.enter="handleConfirm"
            @keydown.escape="handleCancel"
          />
        </div>
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <UiButton variant="secondary" @click="handleCancel">취소</UiButton>
        <UiButton variant="danger" :disabled="!isConfirmValid" @click="handleConfirm">삭제하기</UiButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  tabId: {
    type: String,
    default: ''
  },
  tabLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['confirm', 'cancel']);

const confirmText = ref('');
const confirmInput = ref(null);

// 입력된 텍스트가 탭 라벨과 일치하는지 확인
const isConfirmValid = computed(() => {
  return confirmText.value.trim() === props.tabLabel.trim();
});

// 모달이 열릴 때마다 입력 필드 초기화 및 포커스
watch(
  () => props.isVisible,
  async newValue => {
    if (newValue) {
      confirmText.value = '';
      // DOM 업데이트 후 포커스
      await nextTick();
      if (confirmInput.value) {
        confirmInput.value.focus();
      }
    }
  }
);

const handleConfirm = () => {
  if (isConfirmValid.value) {
    emit('confirm', props.tabId);
  }
};

const handleCancel = () => {
  emit('cancel');
};

const handleOverlayClick = () => {
  handleCancel();
};
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e5e5;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-content {
  padding: 24px;
}

.warning-messages {
  margin-bottom: 24px;
}

.warning-text {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
    font-weight: 600;
    color: #333;
  }
}

.input-section {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.confirm-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00aaff;
    box-shadow: 0 0 0 2px rgba(0, 170, 255, 0.2);
  }

  &::placeholder {
    color: #999;
  }
}

.modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

// UiButton variant 스타일
:deep(.ui-button--secondary) {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;

  &:hover {
    background-color: #e5e5e5;
  }
}

:deep(.ui-button--danger) {
  background-color: #dc3545;
  color: white;
  border: 1px solid #dc3545;

  &:hover:not(:disabled) {
    background-color: #c82333;
    border-color: #bd2130;
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
}
</style>
